import { useMemo } from "react";
import type { GameMode, PlayerPublicState, RevealedSong } from "@hitster/shared";
import { buildPlayerColorMap, getPlayerColor } from "../lib/playerColors";
import { PlayerBoardRow } from "./PlayerBoardRow";
import { Timeline } from "./Timeline";

interface Props {
  players: PlayerPublicState[];
  winTarget: number;
  playerId: string | null;
  canPlace: boolean;
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  roundNumber: number;
  mode?: GameMode;
  sharedTimeline?: RevealedSong[];
}

/** Joueur actif en premier, puis les autres dans l'ordre du serveur. */
function orderForDisplay(players: PlayerPublicState[]): PlayerPublicState[] {
  if (players.length <= 1) return players;
  const idx = players.findIndex((p) => p.isActive);
  if (idx <= 0) return players;
  return [...players.slice(idx), ...players.slice(0, idx)];
}

export function GameTable({
  players,
  winTarget,
  playerId,
  canPlace,
  selectedIndex,
  onSelect,
  roundNumber,
  mode = "individual",
  sharedTimeline,
}: Props) {
  const ordered = orderForDisplay(players);
  const isShared = mode === "shared";
  const timeline = sharedTimeline ?? ordered[0]?.timeline ?? [];

  const playingPlayers = useMemo(
    () => players.filter((p) => p.role === "player"),
    [players],
  );

  const playerColorById = useMemo(
    () => buildPlayerColorMap(playingPlayers.map((p) => p.id)),
    [playingPlayers],
  );

  if (isShared) {
    const activePlayer = ordered.find((p) => p.isActive);
    const isMyTurn = activePlayer?.id === playerId;

    return (
      <div className="space-y-2 sm:space-y-4" aria-label="Plateau de jeu">
        <p className="text-center text-[10px] sm:text-xs uppercase tracking-widest text-violet-400/50">
          Manche {roundNumber}
        </p>

        <section
          className={`cw-panel p-2 sm:p-4 transition-all ${
            activePlayer
              ? "ring-2 ring-orange-400/70 shadow-lg shadow-orange-500/10"
              : ""
          }`}
          aria-label="Frise commune"
        >
          <p className="text-[10px] sm:text-xs uppercase tracking-widest text-violet-400/60 mb-2 text-center">
            Frise commune
            {activePlayer && (
              <span className="text-orange-300 normal-case tracking-normal ml-2">
                — tour de {activePlayer.nickname}
              </span>
            )}
          </p>
          <Timeline
            timeline={timeline}
            selectedIndex={isMyTurn && canPlace ? selectedIndex : null}
            canPlace={isMyTurn && canPlace}
            onSelect={onSelect}
            compact={false}
            playerColorById={playerColorById}
          />
        </section>

        <div className="space-y-2">
          {ordered.map((player) => (
            <PlayerBoardRow
              key={player.id}
              player={player}
              winTarget={winTarget}
              isMe={player.id === playerId}
              canPlace={false}
              selectedIndex={null}
              onSelect={onSelect}
              hideTimeline
              playerColor={getPlayerColor(player.id, playerColorById)}
              showColorDot={player.role === "player"}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-4" aria-label="Plateau de jeu">
      <p className="text-center text-[10px] sm:text-xs uppercase tracking-widest text-violet-400/50">
        Manche {roundNumber}
      </p>

      {ordered.map((player) => {
        const isMe = player.id === playerId;
        return (
          <PlayerBoardRow
            key={player.id}
            player={player}
            winTarget={winTarget}
            isMe={isMe}
            canPlace={canPlace}
            selectedIndex={selectedIndex}
            onSelect={onSelect}
            compact={!player.isActive}
            playerColor={
              player.role === "player" ? getPlayerColor(player.id, playerColorById) : undefined
            }
          />
        );
      })}
    </div>
  );
}
